import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Modal,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import FilterSection from './FilterSection';
import BulkActions from './BulkActions';
import { formatRuleName, formatBulkActions } from '../utils/formatters';
import ErrorSnackbar from './ErrorSnackbar';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '90vh',
  bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(35, 35, 35, 0.95)',
  border: (theme) => `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)'}`,
  borderRadius: 2,
  boxShadow: (theme) => theme.palette.mode === 'light' 
    ? '0px 4px 20px rgba(0, 0, 0, 0.25)' 
    : '0px 4px 20px rgba(0, 0, 0, 0.5)',
  p: 4,
  overflow: 'auto',
};

/**
 * RuleModal component for creating and editing rules
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {Object} props.initialFilters - Initial filters to apply
 * @param {Array} props.initialActions - Initial actions to apply
 * @param {Set} props.allTags - All available tags
 * @param {Function} props.onSave - Function to call when saving the rule
 * @param {number} props.minAmount - Minimum amount for filter range
 * @param {number} props.maxAmount - Maximum amount for filter range
 * @param {Date} props.minDate - Minimum date for filter range
 * @param {Date} props.maxDate - Maximum date for filter range
 * @param {boolean} props.editMode - Whether we're editing an existing rule
 * @param {string|number} props.editRuleId - ID of the rule being edited
 */
const RuleModal = ({ 
  open, 
  onClose, 
  initialFilters, 
  initialActions = [], 
  allTags,
  onSave,
  minAmount,
  maxAmount,
  minDate,
  maxDate,
  editMode = false,
  editRuleId = null,
}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [actions, setActions] = useState(initialActions);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  // Update state when props change, especially when editing a rule
  useEffect(() => {
    if (open) {
      setFilters(initialFilters);
      setActions(initialActions);
    }
  }, [open, initialFilters, initialActions]);

  const handleBulkAction = (action, value) => {
    // Check if this action already exists in the queue
    const isDuplicate = actions.some(existingAction => {
      // For actions without values (like clearComment, tickTax, untickTax)
      if (!value && !existingAction.value) {
        return existingAction.action === action;
      }
      // For actions with values (like addTag, removeTag)
      return existingAction.action === action && existingAction.value === value;
    });
  
    // Only add if it's not a duplicate
    if (!isDuplicate) {
      setActions([...actions, { action, value }]);
    }
    else {
      setErrorMessage('This action is already in the queue');
      setShowError(true);
    }
  };

  const handleSave = () => {
    const generatedName = formatRuleName(filters, actions);
    onSave({
      name: generatedName,
      filters,
      actions,
    }, editMode ? editRuleId : null);
    onClose();
  };

  const removeAction = (index) => {
    const newActions = [...actions];
    newActions.splice(index, 1);
    setActions(newActions);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <ErrorSnackbar
          open={showError}
          message={errorMessage}
          onClose={() => setShowError(false)}
        />
        <Typography 
          variant="h6" 
          component="h2" 
          gutterBottom
        >
          {editMode ? 'Edit Rule' : 'Create Rule'}
        </Typography>
        <FilterSection
          filters={filters}
          setFilters={setFilters}
          allTags={allTags}
          minAmount={minAmount}
          maxAmount={maxAmount}
          minDate={minDate}
          maxDate={maxDate}
          isOpen={true}
          hideActions={true}
        />
        <BulkActions
          onBulkAction={handleBulkAction}
          allTags={allTags}
        />
        {actions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography 
              variant="subtitle2"
            >
              Queued Actions:
            </Typography>
            <List>
              {actions.map((action, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <Button 
                      size="small" 
                      color="error"
                      onClick={() => removeAction(index)}
                    >
                      Remove
                    </Button>
                  }
                >
                  <ListItemText 
                    primary={formatBulkActions(action)}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        <Box sx={{ 
          mt: 3, 
          pt: 2,
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2,
        }}>
          <Button 
            onClick={onClose}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={actions.length === 0}
          >
            {editMode ? 'Update Rule' : 'Save Rule'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RuleModal;